import {
  getSessionCache,
  setSessionCache,
} from "@/scripts/global/utils/helpers"
import { sendEvent } from "@/scripts/global/utils/sendEvent"

const CACHE_PREFIX = {
  search: "hos_search:",
  searchPanel: "hos_search_panel:",
}

const throttle = (fn, wait = 5000) => {
  let lastRun = 0
  return (...args) => {
    const now = Date.now()
    if (now - lastRun < wait) return
    lastRun = now
    fn(...args)
  }
}

const sendSearchEvent = throttle(sendEvent, 5000)

export const Filter = () => ({
  page: 1,
  products: [],
  loadAll: false,
  initialPage: 1,
  checkboxes: [],
  searchTerm: "",
  totalResults: 0,
  isPopState: false,
  productGrid: null,
  searchRequestId: 0,
  selectedFilters: 0,
  selectedView: "2x2",
  loadingAction: null,
  activeController: null,
  hasMoreProducts: false,
  hasPrevProducts: false,
  init() {
    const searchParams = new URLSearchParams(window.location.search)
    this.searchTerm = searchParams.get("q") || ""
    this.page = parseInt(searchParams.get("page") || "1")
    this.initialPage = this.page
    this.productGrid = document.getElementById("product-grid")
    const filterPanel = document.getElementById("filter-panel")
    const isCollectionPage =
      this.productGrid?.dataset?.template === "collection"

    if (this.productGrid) {
      this.products = [...this.productGrid.querySelectorAll(".product-card")]
      this.totalResults = this.productGrid.dataset?.totalResults || 0
      this.hasMoreProducts =
        this.productGrid.dataset?.hasMoreProducts === "true"
      this.hasPrevProducts =
        this.productGrid.dataset?.hasPrevProducts === "true"
    }

    if (filterPanel) {
      this.checkboxes = [
        ...filterPanel.querySelectorAll("input[type='checkbox']"),
      ]
      this.checkboxes.forEach((checkbox) => {
        const isSelected = searchParams
          .getAll(checkbox.name)
          .includes(checkbox.value)
        if (isSelected) this.selectedFilters++
      })
    }
    if (this.searchTerm && isCollectionPage) this.handleSearch()
    if (!isCollectionPage) this.hydrateFilterPanel()

    this._boundHandlePopState = this.handlePopState.bind(this)
    window.addEventListener("popstate", this._boundHandlePopState)
  },
  destroy() {
    window.removeEventListener("popstate", this._boundHandlePopState)
  },
  resetUIState(area) {
    this.page = 1
    const newSearchParams = new URLSearchParams()
    const currentParams = new URLSearchParams(window.location.search)

    if (area === "filters") {
      this.selectedFilters = 0
      this.checkboxes.forEach((checkbox) => (checkbox.checked = false))
      if (currentParams.get("q")) {
        newSearchParams.set("q", currentParams.get("q"))
      }
    } else if (area === "search") {
      this.searchTerm = ""
      currentParams.forEach((value, key) => {
        if (key !== "q") newSearchParams.set(key, value)
      })
    }
    this.handleSearch(newSearchParams)
  },
  handleSearch(newParams) {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const searchParams = new URLSearchParams(
      newParams || window.location.search
    )
    const trimedSearchTerm = this.searchTerm?.trim() || ""
    const removedSearchTerm =
      currentSearchParams.has("q") && trimedSearchTerm.length === 0
    const collectionTag = this.productGrid?.dataset?.collectionTag
    const isCollectionPage =
      this.productGrid?.dataset?.template === "collection"
    const usingRelevanceParam =
      searchParams.has("sort_by") && searchParams.get("sort_by") === "relevance"

    const useSearchAPI =
      removedSearchTerm && isCollectionPage
        ? false
        : trimedSearchTerm.length > 0 ||
          usingRelevanceParam ||
          !isCollectionPage

    const url = new URL(
      useSearchAPI ? "/search" : window.location.pathname,
      window.location.origin
    )

    if (newParams?.has("page")) searchParams.set("page", newParams.get("page"))

    if (removedSearchTerm) {
      searchParams.set("page", "1")
      if (newParams) newParams.delete("page")
    }

    if (useSearchAPI) {
      searchParams.set("q", trimedSearchTerm || "**")
      searchParams.set("type", "product")
      if (collectionTag) {
        searchParams.set("filter.p.tag", collectionTag)
      }
    }
    searchParams.set("view", "meta-data")

    this.updateUrl(newParams)
    url.search = searchParams.toString()
    this.searchProducts(url)
  },
  searchProducts(url, searchPanel = null) {
    const cacheKey = `${
      searchPanel ? CACHE_PREFIX.searchPanel : CACHE_PREFIX.search
    }${url.href}`
    const cachedMarkup = getSessionCache(cacheKey)
    const location =
      this.productGrid?.dataset?.template === "collection"
        ? "Collections"
        : "Search Page"
    const searchQuery = url.searchParams.get("q")

    if (this.activeController) this.activeController.abort()
    this.searchRequestId++
    const requestId = this.searchRequestId

    if (this.page === 1 && cachedMarkup) {
      if (searchPanel) {
        this.renderSearchPanelResults(cachedMarkup)
      } else {
        this.renderSearchResults(cachedMarkup)
      }
      if (searchQuery)
        sendSearchEvent(`${searchPanel ? "Search Panel" : location} - Search`, {
          event_detail: searchQuery,
        })
      return
    }

    this.activeController = new AbortController()
    fetch(url, { signal: this.activeController.signal })
      .then((res) => {
        if (!res.ok) throw Error(`Fetching product error status: ${res.status}`)
        return res.text()
      })
      .then((markup) => {
        if (requestId !== this.searchRequestId) return
        if (this.page === 1) setSessionCache(cacheKey, markup)
        if (searchPanel) {
          this.renderSearchPanelResults(markup)
        } else {
          this.renderSearchResults(markup)
        }
        if (searchQuery)
          sendSearchEvent(
            `${searchPanel ? "Search Panel" : location} - Search`,
            { event_detail: searchQuery }
          )
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          this.activeController = null
          console.error("Aborted previous search request")
        } else {
          console.error(`Search failed:`, error)
        }
      })
      .finally(() => {
        this.activeController = null
        this.loadingAction = null
      })
  },
  renderSearchResults(markup) {
    const dom = new DOMParser().parseFromString(markup, "text/html")
    const results = dom.getElementById("product-results")
    const newFilters = dom.getElementById("filters-json")

    if (results?.dataset) {
      const { hasMoreProducts, totalResults, hasPrevProducts } = results.dataset
      this.hasMoreProducts = hasMoreProducts === "true"
      this.totalResults = totalResults || 0

      if (this.loadingAction === "load-prev") {
        this.hasPrevProducts = hasPrevProducts === "true"
      }
    }

    if (results && this.productGrid) {
      if (this.loadingAction) {
        const originalFirstChild = this.productGrid.firstChild
        results.querySelectorAll(".product-card").forEach((product) => {
          if (this.loadingAction === "load-more") {
            this.productGrid.appendChild(product)
          } else if (this.loadingAction === "load-prev") {
            this.productGrid.insertBefore(product, originalFirstChild)
          }
        })
      } else {
        this.productGrid.innerHTML = results.innerHTML
        if (window.innerWidth < 990) {
          this.productGrid.scrollIntoView({ behavior: "smooth" })
        }
      }
      document.dispatchEvent(new CustomEvent("swym:collections-loaded"))
      window.dispatchEvent(
        new CustomEvent("search:completed", {
          detail: {
            query: this.searchTerm,
            resultCount: this.totalResults,
          },
        })
      )
      this.products = [...this.productGrid.querySelectorAll(".product-card")]
    }

    this.updateFilterState(newFilters)
  },
  handleFilters(e) {
    this.page = 1
    const { value, checked, name } = e.target
    const searchParams = new URLSearchParams(window.location.search)
    const location =
      this.productGrid?.dataset?.template === "collection"
        ? "Collections"
        : "Search Page"

    if (name !== "sort_by") {
      if (checked) {
        this.selectedFilters++
        searchParams.has(name)
          ? searchParams.append(name, value)
          : searchParams.set(name, value)
      } else {
        this.selectedFilters--
        searchParams.delete(name, value)
      }
    } else {
      searchParams.set(name, value)
    }
    sendEvent(
      `${location} - ${name !== "sort_by" ? "Filter" : "Sort"} Selection`,
      {
        event_detail: e.target.dataset.label || value,
        event_detail2: `${checked ? "Checked" : "Unchecked"}`,
      }
    )
    this.handleSearch(searchParams)
  },
  updateUrl(newParams) {
    const url = new URL(window.location)
    const searchParams = new URLSearchParams(
      newParams || window.location.search
    )

    if (this.searchTerm?.trim()) {
      searchParams.set("q", this.searchTerm)
      searchParams.set("sort_by", "relevance")
    } else {
      searchParams.delete("q")
      if (searchParams.get("sort_by") === "relevance") {
        searchParams.delete("sort_by")
      }
    }

    url.search = searchParams.toString()
    if (this.isPopState) {
      this.isPopState = false
      window.history.replaceState(null, "", url)
    } else {
      window.history.pushState(null, "", url)
    }
  },
  updateFilterState(newFilters) {
    if (!newFilters || !newFilters?.textContent) return

    const { filterOptions } = JSON.parse(newFilters.textContent)
    this.checkboxes.forEach((checkbox) => {
      const matchingOption = filterOptions.find((option) => {
        return (
          this.normalizeValues(option.name) ===
            this.normalizeValues(checkbox.name) &&
          this.normalizeValues(option.value) ===
            this.normalizeValues(checkbox.value)
        )
      })

      if (matchingOption) {
        if (matchingOption.count > 0) {
          window.dispatchEvent(
            new CustomEvent("force-open", {
              detail: { accordionId: checkbox.dataset.accordion },
            })
          )
        }
        checkbox.ariaHidden = matchingOption.count === 0
      } else {
        checkbox.ariaHidden = true
      }
    })
  },
  handleLoadingProducts() {
    const searchParams = new URLSearchParams(window.location.search)

    if (this.loadingAction === "load-more") {
      this.page++
      searchParams.set("page", this.page)
    } else {
      this.initialPage--
      searchParams.set("page", Math.max(1, this.initialPage))
    }

    this.handleSearch(searchParams)
  },
  handlePopState() {
    if (window.location.hash === "#swym-list") return
    const searchParams = new URLSearchParams(window.location.search)
    this.searchTerm = searchParams.get("q") || ""
    this.isPopState = true
    this.handleSearch()
  },
  normalizeValues(value) {
    let normalizedValue = value.toString().toLowerCase()
    normalizedValue = normalizedValue.replaceAll("&amp;", "&")
    normalizedValue = normalizedValue.replaceAll("&#39;", "'")

    return normalizedValue
  },
  hydrateFilterPanel() {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set("view", "filters")
    searchParams.set("q", "**")

    fetch(`/search?${searchParams.toString()}`)
      .then((res) => {
        if (!res.ok)
          throw Error(`Fetching filter panel error status: ${res.status}`)
        return res.text()
      })
      .then((markup) => {
        const dom = new DOMParser().parseFromString(markup, "text/html")
        const newFilters = dom.getElementById("filter-options-container")
        const currentFilters = document.getElementById(
          "filter-options-container"
        )

        if (newFilters && currentFilters) {
          const newCheckboxes = newFilters.querySelectorAll(
            "input[type='checkbox']"
          )

          this.selectedFilters = 0
          newCheckboxes.forEach((newCheckbox) => {
            const currentCheckbox = this.checkboxes.find(
              (checkbox) =>
                this.normalizeValues(newCheckbox.name) ===
                  this.normalizeValues(checkbox.name) &&
                this.normalizeValues(newCheckbox.value) ===
                  this.normalizeValues(checkbox.value)
            )

            newCheckbox.ariaHidden = currentCheckbox
              ? currentCheckbox.ariaHidden
              : true
          })
          currentFilters.innerHTML = newFilters.innerHTML
          this.checkboxes = [
            ...currentFilters.querySelectorAll("input[type='checkbox']"),
          ]
        }
      })
      .catch((error) => {
        console.error(`Hydrate filter panel failed:`, error)
      })
  },
  renderSearchPanelResults(newMarkup) {
    const dom = new DOMParser().parseFromString(newMarkup, "text/html")
    const results = dom.getElementById("product-results")
    const searchResultsContainer = document.getElementById(
      "search-panel-results"
    )

    if (results && searchResultsContainer) {
      window.dispatchEvent(
        new CustomEvent("search-panel:results-updated", {
          detail: { totalResults: results.dataset.totalResults },
        })
      )
      searchResultsContainer.innerHTML = results.innerHTML
      document.dispatchEvent(new CustomEvent("swym:collections-loaded"))
    }
  },
})
